import { invoke } from '@tauri-apps/api/core';
import { listen } from '@tauri-apps/api/event';
import { openPath, revealItemInDir } from '@tauri-apps/plugin-opener';
import { create } from 'zustand';

const MAX_LOG_LINES = 1000;
let listenerPromise = null;

// Parse log line format: [TIMESTAMP][LEVEL][SOURCE] MESSAGE
const parseLogLine = (line) => {
  const trimmed = line.trim();
  const pattern = /^\[([^\]]+)\]\[([^\]]+)\]\[([^\]]+)\]\s*(.*)$/;
  const match = trimmed.match(pattern);

  if (!match) {
    // Fallback for malformed lines
    return {
      timestamp: new Date().toISOString(),
      level: 'INFO',
      source: 'UNKNOWN',
      message: trimmed,
    };
  }

  return {
    timestamp: match[1],
    level: match[2],
    source: match[3],
    message: match[4],
  };
};

export const useLogStore = create((set, get) => ({
  // State
  logLines: [],
  isFollowing: true,
  levelFilter: 'ERROR',
  sourceFilter: 'all',

  // Actions
  initListener: () => {
    // Singleton - reuse same promise across StrictMode remounts
    if (!listenerPromise) {
      listenerPromise = listen('log-line', (event) => {
        const line = event.payload;
        if (typeof line === 'string' && line.trim()) {
          get().appendLog(line);
        }
      }).catch((err) => {
        console.error('Failed to init log listener:', err);
        listenerPromise = null;
      });
    }
    return listenerPromise;
  },

  loadInitialLogs: async () => {
    try {
      const content = await invoke('read_log_file');
      if (!content) return;

      const lines = content.split('\n').filter((l) => l.trim());
      const parsed = lines.slice(-MAX_LOG_LINES).map(parseLogLine);

      set({ logLines: parsed });
    } catch (err) {
      console.error('Failed to load initial logs:', err);
    }
  },

  appendLog: (line) =>
    set((state) => {
      const parsed = parseLogLine(line);
      const newLines = [...state.logLines, parsed];
      // Keep only last MAX_LOG_LINES
      return { logLines: newLines.slice(-MAX_LOG_LINES) };
    }),

  openLogFile: async () => {
    try {
      const path = await invoke('get_log_path');
      if (path) {
        await openPath(path);
      }
    } catch (err) {
      console.error('Failed to open log file:', err);
    }
  },

  openLogFolder: async () => {
    try {
      const path = await invoke('get_log_path');
      if (path) {
        await revealItemInDir(path);
      }
    } catch (err) {
      console.error('Failed to open log folder:', err);
    }
  },

  clearLogs: () => set({ logLines: [] }),

  setLevelFilter: (level) => set({ levelFilter: level }),

  setSourceFilter: (source) => set({ sourceFilter: source }),

  toggleFollowing: () => set((state) => ({ isFollowing: !state.isFollowing })),

  // Frontend logging wrappers
  logError: async (message) => {
    try {
      await invoke('log_from_frontend', {
        level: 'ERROR',
        message,
      });
    } catch (err) {
      console.error('Failed to log error:', err);
    }
  },

  logWarn: async (message) => {
    try {
      await invoke('log_from_frontend', {
        level: 'WARN',
        message,
      });
    } catch (err) {
      console.error('Failed to log warn:', err);
    }
  },

  logInfo: async (message) => {
    try {
      await invoke('log_from_frontend', {
        level: 'INFO',
        message,
      });
    } catch (err) {
      console.error('Failed to log info:', err);
    }
  },

  logDebug: async (message) => {
    try {
      await invoke('log_from_frontend', {
        level: 'DEBUG',
        message,
      });
    } catch (err) {
      console.error('Failed to log debug:', err);
    }
  },

  // Computed selector
  filteredLogs: () => {
    const { logLines, levelFilter, sourceFilter } = get();

    return logLines.filter((log) => {
      const levelMatch = levelFilter === 'all' || log.level === levelFilter;
      const sourceMatch = sourceFilter === 'all' || log.source === sourceFilter;
      return levelMatch && sourceMatch;
    });
  },
}));
